import {
  opportunitiesPartialUpdateMutation,
  opportunitiesPipelineListInfiniteOptions,
  opportunitiesPipelineListInfiniteQueryKey,
  opportunitiesRetrieveOptions,
  opportunitiesStatusesListInfiniteOptions,
  opportunitiesStatusesListInfiniteQueryKey,
  ReturnOpportunityReadable
} from '@crm/api'
import { KanbanComments, OpportunityHeader } from '@crm/components'
import { AddContractModal } from '@crm/components/OpportunityStatuses/AddContractModal'
import { OpportunityRefusalReasonModal } from '@crm/components/OpportunityStatuses/OpportunityRefusalReasonModal'
import { useKanbanLeadsQueryParams } from '@crm/features/leads/hooks'
import {
  OpportunitiesFilter,
  OpportunityKanbanCard
} from '@crm/features/opportunities/components'
import { useStyle } from '@crm/features/opportunities/styles'
import { ActionOpportunityData } from '@crm/features/opportunities/types'
import { Kanban, TitleSection } from '@shared/components'
import { useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import {
  OnDragEndWithRequiresContract,
  OnDragEndWithRequiresRefusalReason
} from '@shared/types'
import { useQuery } from '@tanstack/react-query'
import { Flex, Modal } from 'antd'
import { useState } from 'react'

import { OpportunitiesCreatePage } from '../OpportunitiesCreatePage'

export const OpportunitiesKanban = () => {
  const { t } = useLocales()
  const queryParams = useQueryParams()
  const kanbanQueryParams = useKanbanLeadsQueryParams()
  const { styles } = useStyle()

  const [actionOpportunityData, setActionOpportunityData] =
    useState<ActionOpportunityData>({
      type: undefined,
      open: false,
      opportunityId: undefined
    })

  const [refusalReasonModal, setRefusalReasonModal] = useState<{
    open: boolean
    opportunityId?: number
    onConfirm?: () => void
    rollback?: () => void
  }>({ open: false })

  const [contractModal, setContractModal] = useState<{
    open: boolean
    opportunityId?: number
    onConfirm?: () => void
    rollback?: () => void
  }>({ open: false })

  const { data: opportunity } = useQuery({
    ...opportunitiesRetrieveOptions({
      path: { id: Number(actionOpportunityData?.opportunityId) }
    }),
    enabled: !!Number(actionOpportunityData?.opportunityId)
  })

  const onActionClick = (actionData: ActionOpportunityData) =>
    setActionOpportunityData(actionData)

  const onCancelModal = () => {
    setActionOpportunityData({
      open: false,
      type: undefined,
      opportunityId: undefined
    })
  }

  const onDragEndWithRequiresRefusalReason = ({
    kanbanId,
    statusId,
    updateStatus,
    rollback,
    fromQueryKey,
    toQueryKey
  }: OnDragEndWithRequiresRefusalReason) => {
    setRefusalReasonModal({
      open: true,
      opportunityId: Number(kanbanId),
      rollback,
      onConfirm: () =>
        updateStatus({ status: statusId }, [fromQueryKey, toQueryKey])
    })
  }

  const onDragEndWithRequiresContract = ({
    kanbanId,
    statusId,
    updateStatus,
    rollback,
    fromQueryKey,
    toQueryKey
  }: OnDragEndWithRequiresContract) => {
    setContractModal({
      open: true,
      opportunityId: Number(kanbanId),
      rollback,
      onConfirm: () =>
        updateStatus({ status: statusId }, [fromQueryKey, toQueryKey])
    })
  }

  return (
    <>
      <TitleSection title={t(`lead.opportunities`)}></TitleSection>

      <Kanban
        header={
          <Flex justify="space-between">
            <Flex align="start" wrap gap={8}>
              <OpportunitiesFilter />
            </Flex>

            <OpportunityHeader />
          </Flex>
        }
        cardRender={(data: ReturnOpportunityReadable) => (
          <OpportunityKanbanCard data={data} onActionClick={onActionClick} />
        )}
        statusesQuery={opportunitiesStatusesListInfiniteOptions}
        statusesQueryKey={opportunitiesStatusesListInfiniteQueryKey}
        pipelineListQuery={opportunitiesPipelineListInfiniteOptions}
        pipelineListUpdateMutation={opportunitiesPartialUpdateMutation}
        pipelineListQueryKey={opportunitiesPipelineListInfiniteQueryKey}
        pipelineListQueryOptions={kanbanQueryParams}
        extraStatusQueryOptions={{ pipeline: queryParams.pipeline }}
        onDragEndWithRequiresRefusalReason={onDragEndWithRequiresRefusalReason}
        onDragEndWithRequiresContract={onDragEndWithRequiresContract}
      >
        <Modal
          open={actionOpportunityData?.type === 'edit'}
          onCancel={onCancelModal}
          width={1200}
          footer={false}
          className={styles.modal}
        >
          <OpportunitiesCreatePage
            opportunityDetails={opportunity}
            onCancelModal={onCancelModal}
          />
        </Modal>

        <Modal
          open={actionOpportunityData?.type === 'comments'}
          onCancel={onCancelModal}
          width={700}
          footer={false}
          className={styles.modal}
        >
          <KanbanComments
            type="opportunity"
            id={Number(actionOpportunityData?.opportunityId)}
            onCancel={onCancelModal}
          />
        </Modal>
      </Kanban>

      <OpportunityRefusalReasonModal
        open={refusalReasonModal.open}
        opportunityId={refusalReasonModal.opportunityId}
        onConfirm={() => {
          refusalReasonModal.onConfirm?.()
          setRefusalReasonModal({ open: false })
        }}
        onCancel={() => {
          refusalReasonModal.rollback?.()
          setRefusalReasonModal({ open: false })
        }}
      />

      <AddContractModal
        open={contractModal.open}
        opportunityId={contractModal.opportunityId}
        onSuccess={() => {
          contractModal.onConfirm?.()
          setContractModal({ open: false })
        }}
        onCancel={() => {
          contractModal.rollback?.()
          setContractModal({ open: false })
        }}
      />
    </>
  )
}
